import { useFullReleaseStrict } from "@/common/hooks_useFullRelease";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import { type DecisionGraph, formatterSuggestions } from "@imminently/decisively-core";
import Field from "@imminently/decisively-ui/Field";
import { useField } from "@imminently/decisively-ui/Form";
import MonacoEditor from "@monaco-editor/react";
import { useEffect, useMemo, useRef } from "react";
import type React from "react";

const PARENT_TEMPLATE_PREFIX = "@parent";
/** Every formatter the server understands, from decisively-core's registry. */
const DEFAULT_TEMPLATE_FORMATTERS = formatterSuggestions();

type PlainTextTemplateContext = {
  mode: "attribute" | "formatter";
  searchTerm: string;
  replaceStart: number;
};

export type TemplateFieldProps = {
  label?: React.ReactNode;
  name: string;
  value?: string;
  onValueChange?: (value: string) => void;
  graph?: DecisionGraph;
  attributeEntity?: string;
  disabled?: boolean;
  rows?: number;
  required?: boolean;
  helperText?: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  attributeSuggestionValue?: "description" | "path";
};

const getTemplateCompletionContext = (text: string, offset: number): PlainTextTemplateContext | null => {
  const textUpToCursor = text.slice(0, offset);
  const openIdx = textUpToCursor.lastIndexOf("{{");
  if (openIdx < 0) return null;

  const closeIdx = textUpToCursor.lastIndexOf("}}");
  if (closeIdx > openIdx) return null;

  const contentBeforeCursor = textUpToCursor.slice(openIdx + 2);
  const lastPipeIdx = contentBeforeCursor.lastIndexOf("|");

  if (lastPipeIdx < 0) {
    const leadingWhitespace = contentBeforeCursor.length - contentBeforeCursor.trimStart().length;
    return {
      mode: "attribute",
      searchTerm: contentBeforeCursor.trim(),
      replaceStart: openIdx + 2 + leadingWhitespace,
    };
  }

  const currentFormatterChunk = contentBeforeCursor.slice(lastPipeIdx + 1);
  const leadingWhitespace = currentFormatterChunk.length - currentFormatterChunk.trimStart().length;
  return {
    mode: "formatter",
    searchTerm: currentFormatterChunk.trim(),
    replaceStart: openIdx + 2 + lastPipeIdx + 1 + leadingWhitespace,
  };
};

const getCaseInsensitiveMatch = (items: string[], value: string): string | undefined => {
  const lowerValue = value.toLowerCase();
  return items.find((item) => item.toLowerCase() === lowerValue);
};

const getCaseInsensitiveMapKey = (map: Map<string, unknown>, key: string): string | undefined => {
  const lowerKey = key.toLowerCase();
  for (const existingKey of map.keys()) {
    if (existingKey.toLowerCase() === lowerKey) return existingKey;
  }
  return undefined;
};

const scoreSuggestion = (candidate: string, searchTerm: string): number => {
  if (!searchTerm) return 0;
  const lcCandidate = candidate.toLowerCase();
  const lcSearch = searchTerm.toLowerCase();
  if (lcCandidate.startsWith(lcSearch)) return 0;
  const containsIdx = lcCandidate.indexOf(lcSearch);
  if (containsIdx >= 0) return containsIdx + 1;
  return Number.MAX_SAFE_INTEGER;
};

const sortSuggestions = (items: string[], searchTerm: string) => {
  return items.sort((a, b) => {
    const scoreA = scoreSuggestion(a, searchTerm);
    const scoreB = scoreSuggestion(b, searchTerm);
    if (scoreA !== scoreB) return scoreA - scoreB;
    return a.localeCompare(b);
  });
};

const getTemplateAttributeSuggestions = ({
  searchTerm,
  attributes,
  entities,
  attributesByEntity,
}: {
  searchTerm: string;
  attributes: string[];
  entities: string[];
  attributesByEntity: Map<string, string[]>;
}): string[] => {
  const trimmed = searchTerm.trim();
  const lower = trimmed.toLowerCase();
  const parentPrefix = PARENT_TEMPLATE_PREFIX.toLowerCase();

  if (!lower.startsWith("@")) {
    return sortSuggestions(
      attributes.filter((item) => item.toLowerCase().includes(lower)),
      trimmed,
    );
  }

  if (parentPrefix.startsWith(lower) || lower.startsWith(parentPrefix)) {
    if (!lower.startsWith(`${parentPrefix}/`)) {
      return `${PARENT_TEMPLATE_PREFIX}/`.toLowerCase().includes(lower) ? [`${PARENT_TEMPLATE_PREFIX}/`] : [];
    }

    const remainder = trimmed.slice(PARENT_TEMPLATE_PREFIX.length + 1);
    const separator = remainder.indexOf("/");
    if (separator < 0) {
      const entityTerm = remainder.trim();
      return sortSuggestions(
        entities.filter((entity) => entity.toLowerCase().includes(entityTerm.toLowerCase())),
        entityTerm,
      ).map((entity) => `${PARENT_TEMPLATE_PREFIX}/${entity}/`);
    }

    const rawEntity = remainder.slice(0, separator).trim();
    const attributeTerm = remainder.slice(separator + 1).trim();
    const matchedEntity =
      getCaseInsensitiveMatch(entities, rawEntity) ?? getCaseInsensitiveMapKey(attributesByEntity, rawEntity);

    if (!matchedEntity) {
      return sortSuggestions(
        entities.filter((entity) => entity.toLowerCase().includes(rawEntity.toLowerCase())),
        rawEntity,
      ).map((entity) => `${PARENT_TEMPLATE_PREFIX}/${entity}/`);
    }

    const scopedAttributes = attributesByEntity.get(matchedEntity) ?? [];
    return sortSuggestions(
      scopedAttributes.filter((attribute) => attribute.toLowerCase().includes(attributeTerm.toLowerCase())),
      attributeTerm,
    ).map((attribute) => `${PARENT_TEMPLATE_PREFIX}/${matchedEntity}/${attribute}`);
  }

  return [];
};

export const TemplateField = ({
  label,
  name,
  value,
  onValueChange,
  graph: graphOverride,
  attributeEntity,
  disabled = false,
  rows = 1,
  required,
  helperText,
  fullWidth = true,
  className,
  attributeSuggestionValue = "description",
}: TemplateFieldProps) => {
  const field = useField<string>({ name, value, onValueChange, required });
  const contextGraph = useParsedGraph();
  const graph = graphOverride ?? contextGraph;
  const release = useFullReleaseStrict();
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const completionProviderRef = useRef<{ dispose: () => void } | null>(null);
  const editorHeight = Math.max(48, rows * 24 + 28);

  const attributeItemsByEntity = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const nodeId of graph?.ids() ?? []) {
      const entityField = graph.field(nodeId, "entity");
      const entity = typeof entityField === "string" ? entityField : "";
      if (attributeEntity && entity !== attributeEntity) continue;
      const suggestion =
        attributeSuggestionValue === "path"
          ? graph.field(nodeId, "path") || graph.field(nodeId, "id")
          : graph.field(nodeId, "description");
      if (!entity || typeof suggestion !== "string" || !suggestion.trim()) continue;

      const existing = map.get(entity) ?? [];
      if (!existing.includes(suggestion)) {
        existing.push(suggestion);
        map.set(entity, existing);
      }
    }
    return map;
  }, [attributeEntity, attributeSuggestionValue, graph]);

  const attributeItems = useMemo(() => {
    const allAttributes = [...attributeItemsByEntity.values()].flat();
    return Array.from(new Set(allAttributes));
  }, [attributeItemsByEntity]);

  const entityItems = useMemo(() => {
    const relationshipEntities = (release?.relationships ?? [])
      .map((relationship: any) => relationship?.name)
      .filter((name: unknown): name is string => typeof name === "string" && Boolean(name.trim()));
    return Array.from(new Set([...relationshipEntities, ...attributeItemsByEntity.keys()]));
  }, [release?.relationships, attributeItemsByEntity]);

  const registerCompletionProvider = (editor: any, monaco: any) => {
    completionProviderRef.current?.dispose();
    completionProviderRef.current = monaco.languages.registerCompletionItemProvider("plaintext", {
      triggerCharacters: ["{", "|"],
      provideCompletionItems: (model: any, position: any) => {
        const currentModel = editor.getModel?.();
        if (!currentModel || currentModel.uri.toString() !== model.uri.toString()) {
          return { suggestions: [] };
        }

        const text = model.getValue();
        const offset = model.getOffsetAt(position);
        const context = getTemplateCompletionContext(text, offset);
        if (!context) return { suggestions: [] };

        const sourceItems =
          context.mode === "attribute"
            ? getTemplateAttributeSuggestions({
                searchTerm: context.searchTerm,
                attributes: attributeItems,
                entities: entityItems,
                attributesByEntity: attributeItemsByEntity,
              })
            : sortSuggestions(
                DEFAULT_TEMPLATE_FORMATTERS.filter((item) =>
                  item.toLowerCase().includes(context.searchTerm.toLowerCase()),
                ),
                context.searchTerm,
              );
        const ordered = sourceItems.slice(0, 40);

        if (!ordered.length) return { suggestions: [] };

        const start = model.getPositionAt(context.replaceStart);
        const range = {
          startLineNumber: start.lineNumber,
          startColumn: start.column,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        };

        return {
          suggestions: ordered.map((item) => ({
            label: item,
            kind:
              context.mode === "attribute"
                ? monaco.languages.CompletionItemKind.Variable
                : monaco.languages.CompletionItemKind.Function,
            insertText: item,
            range,
            detail: context.mode === "attribute" ? "Template attribute" : "Template formatter",
          })),
        };
      },
    });
  };

  useEffect(() => {
    if (editorRef.current && monacoRef.current) {
      registerCompletionProvider(editorRef.current, monacoRef.current);
    }

    return () => {
      completionProviderRef.current?.dispose();
      completionProviderRef.current = null;
    };
  }, [attributeItems, entityItems, attributeItemsByEntity]);

  return (
    <Field
      className={className}
      name={name}
      fullWidth={fullWidth}
    >
      {label ? <Field.Label required={required ?? field.required}>{label}</Field.Label> : null}
      <Field.Control>
        <div
          style={{
            height: `${editorHeight}px`,
            border: "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: "4px",
            overflow: "visible",
            padding: "8px",
            position: "relative",
            zIndex: 2,
            backgroundColor: disabled ? "rgba(0, 0, 0, 0.04)" : "transparent",
          }}
        >
          <MonacoEditor
            height="100%"
            defaultLanguage="plaintext"
            value={field.value || ""}
            onChange={(newValue) => field.onValueChange(newValue ?? "")}
            onMount={(editor, monaco) => {
              editorRef.current = editor;
              monacoRef.current = monaco;
              registerCompletionProvider(editor, monaco);
            }}
            options={{
              readOnly: disabled,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              lineNumbers: "off",
              glyphMargin: false,
              folding: false,
              lineDecorationsWidth: 0,
              lineNumbersMinChars: 0,
              renderLineHighlight: "none",
              overviewRulerLanes: 0,
              hideCursorInOverviewRuler: true,
              scrollbar: { vertical: "auto", horizontal: "hidden" },
              fontSize: 14,
              fontFamily: "Montserrat, Helvetica Neue, Arial, sans-serif",
              wordWrap: "on",
              padding: { top: 6, bottom: 6 },
              quickSuggestions: { strings: true, comments: false, other: true },
              suggestOnTriggerCharacters: true,
              acceptSuggestionOnEnter: "on",
              tabCompletion: "on",
              automaticLayout: true,
            }}
          />
        </div>
      </Field.Control>
      <Field.Message message={helperText} />
    </Field>
  );
};
